import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

export const DEFAULT_ZERO_DAY_TASKS = [
  {
    id: 1,
    name: 'set_plan',
    desc: 'Формирование архитектурного плана аудита и инициализация целей',
    descEn: 'Architectural audit planning and target socket initialization',
    time: '—'
  },
  {
    id: 2,
    name: 'run_python_script',
    desc: 'Разведка топологии сети и анализ сетевого стека',
    descEn: 'Network topology reconnaissance and network stack analysis',
    time: '—'
  },
  {
    id: 3,
    name: 'run_nodejs_script',
    desc: 'Сканирование открытых портов, RPC-мостов и API',
    descEn: 'Scanning open ports, RPC bridges and internal APIs',
    time: '—'
  },
  {
    id: 4,
    name: 'run_python_script',
    desc: 'Анализ пакетов рукопожатия и токенов сессии',
    descEn: 'Handshake packet inspection and session token analysis',
    time: '—'
  },
  {
    id: 5,
    name: 'run_python_script',
    desc: 'Инспекция десериализации Netty и переполнения памяти',
    descEn: 'Netty deserialization inspection and buffer probe',
    time: '—'
  },
  {
    id: 6,
    name: 'run_nodejs_script',
    desc: 'Синтез полиморфного Zero-Day эксплойта',
    descEn: 'Polymorphic Zero-Day exploit synthesis',
    time: '—'
  },
  {
    id: 7,
    name: 'run_python_script',
    desc: 'Инжекция payload и валидация закрепления доступа',
    descEn: 'Payload delivery and access persistence validation',
    time: '—'
  }
];

const ZeroDayProgressCard = ({
  tasks = [],
  activeTaskIndex = 0,
  agentState = 'idle', // 'idle' | 'running' | 'paused' | 'completed'
  progressPercent = 0
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  const totalTasks = tasks?.length || 0;
  const currentStepDisplay = totalTasks > 0 
    ? (agentState === 'completed' 
        ? `${totalTasks} / ${totalTasks}`
        : `${Math.min(activeTaskIndex + 1, totalTasks)} / ${totalTasks}`)
    : '—';
  const displayPercent = totalTasks > 0 ? progressPercent : 0;

  return (
    <aside className="ai-progress-card">
      <div className="progress-head-row">
        <div className="progress-title-wrap">
          <h3 className="progress-card-title">
            {isEn ? 'AI Progress' : 'Прогресс ИИ'}
          </h3>
        </div>
        <span className="progress-step-counter">
          {currentStepDisplay}
        </span>
      </div>

      {/* Overall Progress */}
      <div className="overall-progress-box">
        <div className="progress-metrics-line">
          <span>{isEn ? 'Plan execution' : 'Выполнение плана'}</span>
          <span>{displayPercent}%</span>
        </div>
        <div className="progress-track">
          <div
            className="progress-fill"
            style={{ width: `${displayPercent}%` }}
          />
        </div>
      </div>

      {/* Tasks List or Empty State */}
      <div className="tasks-list-wrap">
        {tasks && tasks.length > 0 ? (
          tasks.map((task, index) => {
            let itemStatus = task.status;
            if (!itemStatus) {
              if (agentState === 'completed') {
                itemStatus = 'completed';
              } else if (agentState === 'running' || agentState === 'paused') {
                if (index < activeTaskIndex) itemStatus = 'completed';
                else if (index === activeTaskIndex) itemStatus = 'running';
                else itemStatus = 'pending';
              } else {
                itemStatus = 'pending';
              }
            }

            const taskName = task.title || task.name || `Task ${index + 1}`;
            const taskDesc = isEn ? (task.descEn || task.description || task.desc) : (task.desc || task.description || task.descEn);

            let timeText = task.time || '—';
            if (itemStatus === 'running') timeText = isEn ? 'Running...' : 'Выполняется...';
            else if (itemStatus === 'completed' && (!task.time || task.time === '—')) timeText = '0.8s';

            return (
              <div key={task.id || index} className={`task-item ${itemStatus}`}>
                <div className="task-icon-wrap">
                  {itemStatus === 'completed' ? (
                    <svg viewBox="0 0 24 24">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <span className="num">{index + 1}</span>
                  )}
                </div>

                <div className="task-content">
                  <div className="task-title-line">
                    <span className="task-name" title={taskName}>{taskName}</span>
                    <span className="task-time-badge">{timeText}</span>
                  </div>
                  <p className="task-desc" title={taskDesc}>{taskDesc}</p>
                </div>
              </div>
            );
          })
        ) : (
          <div className="tasks-empty-state">
            <div className={`tasks-empty-icon-wrap ${agentState === 'running' ? 'pulsing' : ''}`}>
              {agentState === 'running' ? (
                <svg viewBox="0 0 24 24" className="spinning-slow" fill="none" stroke="currentColor">
                  <circle cx="12" cy="12" r="9" strokeWidth="1.8" strokeDasharray="14 6" />
                  <circle cx="12" cy="12" r="3" fill="currentColor" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              )}
            </div>

            {agentState === 'running' && (
              <span className="tasks-empty-tag thinking-text-shimmer">
                {isEn ? 'AWAITING AI PIPELINE' : 'ФОРМИРОВАНИЕ ПАЙПЛАЙНА'}
              </span>
            )}

            <div className="tasks-empty-text-group">
              {agentState !== 'running' && (
                <h4 className="tasks-empty-title">
                  {isEn ? 'Plan Not Formulated' : 'План ещё не сформирован'}
                </h4>
              )}
              <p className="tasks-empty-desc">
                {agentState === 'running'
                  ? (isEn 
                      ? 'The AI agent is inspecting target architecture and assembling audit tasks in real time.' 
                      : 'ИИ-агент исследует архитектуру цели и в реальном времени формирует задачи аудита.')
                  : (isEn 
                      ? 'Launch the Zero-Day audit to generate a tailored multi-stage attack and reconnaissance pipeline.' 
                      : 'Запустите Zero-Day аудит, чтобы ИИ проанализировал цель и выстроил персональный список задач.')}
              </p>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
};

export default ZeroDayProgressCard;
